import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WidgetOptions } from '../../dashboardTypes';
import DraggableComponent from '../../DraggableComponent';

export type ElectronicsSectionDraggableWidget = {
  id: string;
  type: string;
  dataKey: string;
  title: string;
  minH?: number;
  minW?: number;
  style?: string;
  options?: WidgetOptions;
};

interface ElectronicsSectionCollapsibleProps {
  sectionTitle: string;
  widgets?: ElectronicsSectionDraggableWidget[];
  children?: ReactNode;
}

const ElectronicsSectionCollapsible = ({ sectionTitle, widgets = [], children }: ElectronicsSectionCollapsibleProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const hasContent = widgets.length > 0 || children != null;

  if (!hasContent) {
    return (
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {sectionTitle}
      </Typography>
    );
  }

  return (
    <Stack sx={{ width: '100%' }} alignItems="stretch">
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          {sectionTitle}
        </Typography>
        <IconButton
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? t('aria-label.collapse', 'collapse') : t('aria-label.expand', 'expand')}
          sx={{
            flexShrink: 0,
            padding: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Divider sx={{ width: '100%', my: 1 }} />
        {widgets.length > 0 && (
          <Stack alignItems="center" spacing={1} sx={{ pt: 1, pb: 0.5 }}>
            {widgets.map((widget) => (
              <DraggableComponent
                key={widget.id}
                id={widget.id}
                type={widget.type}
                dataKey={widget.dataKey}
                title={widget.title}
                minH={widget.minH ?? 3}
                minW={widget.minW ?? 4}
                style={widget.style}
                options={widget.options}
              />
            ))}
          </Stack>
        )}
        {children}
      </Collapse>
    </Stack>
  );
};

export default ElectronicsSectionCollapsible;
